import React, { useId, type ComponentType } from "react";
import {
  autoUpdate,
  flip,
  FloatingPortal,
  size,
  useFloating,
} from "@floating-ui/react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { Listbox } from "@headlessui/react";
import { type Noop, type RefCallBack } from "react-hook-form";
import { twJoin, twMerge } from "tailwind-merge";
import {
  ChevronIcon,
  ExclamationCircleIcon,
  LoaderIcon,
} from "@components/icons";
import { Label } from "@components/Label/Label";

type TValue = string | number | boolean;
type TLabel = string | number;

type TSelectOption = {
  /**
   * Option value.
   */
  value: TValue;
  /**
   * Option label.
   */
  label: TLabel;
  /**
   * Disabled option flag.
   */
  disabled?: boolean;
  /**
   * Option icon element.
   */
  icon?: ComponentType<React.SVGProps<SVGSVGElement>>;
};

export interface ISelectProps {
  /**
   * Select field name.
   */
  name?: string;
  /**
   * Available options array.
   */
  options: TSelectOption[];
  /**
   * Selected option value.
   */
  value?: TValue;
  /**
   * Select field label.
   */
  label?: string | JSX.Element;
  /**
   * Select box placeholder.
   */
  placeholder?: string;
  /**
   * Value change callback.
   */
  onChange?: (value?: TValue) => void;
  /**
   * Field blur callback.
   */
  onBlur?: Noop;
  /**
   * Disabled field flag.
   */
  disabled?: boolean;
  /**
   * Field error text.
   */
  error?: string;
  /**
   * Field hint text.
   */
  hint?: string;
  /**
   * Select field size.
   */
  height?: "small" | "medium";
  /**
   * Ref.
   */
  buttonRef?: RefCallBack;
  /**
   * Input field icon.
   */
  icon?: ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Select field classname.
   */
  className?: string;
  /**
   * Whether select content should stay in fixed position.
   */
  isPositionFixed?: boolean;
  /**
   * Select loading state flag.
   */
  isLoading?: boolean;
  /**
   * Whether options should be truncated.
   */
  shouldTruncateOptions?: boolean;
  /**
   * Whether multiple options can be selected.
   */
  isMultiple?: boolean;
}

export const Select: React.FC<ISelectProps> = ({
  options,
  value,
  name,
  label,
  placeholder,
  onChange,
  onBlur,
  disabled,
  buttonRef,
  error,
  hint,
  height = "medium",
  icon: Icon,
  className,
  isPositionFixed,
  isLoading,
  shouldTruncateOptions = true,
  isMultiple = false,
}) => {
  const [animationParent] = useAutoAnimate({ duration: 100 });
  const selectedOptionLabel =
    isMultiple && Array.isArray(value)
      ? options
          .filter((op) => value.includes(op.value))
          .map((op) => op.label)
          .join("; ")
      : options.find((o) => o.value === value)?.label;
  const buttonId = useId();

  const { refs, floatingStyles } = useFloating({
    strategy: isPositionFixed ? "fixed" : "absolute",
    middleware: [
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
      flip(),
    ],
    whileElementsMounted: autoUpdate,
  });

  // eslint-disable-next-line
  const isDisabled = disabled || isLoading;

  return (
    <div className={twMerge("flex flex-col gap-1.5", className)}>
      {label && <Label htmlFor={buttonId}>{label}</Label>}
      <Listbox
        value={value}
        onChange={(option) => onChange?.(option)}
        disabled={isDisabled}
        multiple={isMultiple}
      >
        {({ open }) => (
          <div ref={refs.setReference}>
            <div className="relative" ref={animationParent}>
              <Listbox.Button
                id={buttonId}
                name={name}
                ref={buttonRef}
                className={twMerge(
                  "relative h-11 w-full rounded-full border border-grey-hover pl-3.5 pr-9 text-left text-base leading-6 text-base-black focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-dark focus-visible:ring-opacity-10 active:border-green-dark active:ring-4 active:ring-green-dark active:ring-opacity-10",
                  height === "small" && "h-10 py-2",
                  open &&
                    "border-green-dark ring-4 ring-green-dark ring-opacity-10",
                  isDisabled &&
                    "border-grey-hover bg-base-light-grey text-grey-dark active:border-grey-hover active:ring-0",
                  error &&
                    "border-red-dark pr-16 ring-red-dark focus-visible:border-red-dark focus-visible:ring-red-dark active:border-red-dark active:ring-red-dark",
                )}
                onBlur={onBlur}
              >
                {Icon && (
                  <Icon className="absolute inset-y-0 m-auto size-5 text-grey-dark" />
                )}
                {selectedOptionLabel ? (
                  <span className={twMerge("block truncate", Icon && "ml-8")}>
                    {selectedOptionLabel}
                  </span>
                ) : (
                  <span
                    className={twMerge(
                      "block truncate text-grey-dark",
                      Icon && "ml-8",
                    )}
                  >
                    {placeholder}
                  </span>
                )}
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  {isLoading && (
                    <LoaderIcon
                      className={twJoin("h-5 w-5 animate-spin text-grey-dark")}
                    />
                  )}
                  {!isLoading && (
                    <ChevronIcon
                      className={twJoin(
                        "h-5 w-5 text-grey-dark transition-transform duration-100",
                        open && "rotate-180",
                      )}
                    />
                  )}
                </span>
                {error && (
                  <span className="absolute inset-y-0 right-9 flex size-5 h-full items-center text-red-dark">
                    <ExclamationCircleIcon className="size-5" />
                  </span>
                )}
              </Listbox.Button>
              <FloatingPortal>
                <Listbox.Options
                  ref={refs.setFloating}
                  style={floatingStyles}
                  className="pointer-events-auto absolute z-[9999] mt-2 flex max-h-60 w-full flex-col gap-1 overflow-auto rounded-xl border border-grey-light bg-white p-2 text-base shadow-lg focus:outline-none"
                >
                  {options.map((option, optionIndex) => (
                    <Listbox.Option
                      key={optionIndex}
                      disabled={option.disabled}
                      className={({ active }) =>
                        twMerge(
                          "relative flex select-none flex-row items-center rounded-full px-4 py-2",
                          !option.disabled && "cursor-pointer",
                          ((isMultiple &&
                            Array.isArray(value) &&
                            value.includes(option.value)) ||
                            option.value === value) &&
                            "bg-blue-light text-blue-dark",
                          active && "bg-base-light-grey",
                          option.icon && "gap-2",
                          option.disabled && "bg-none text-grey-hover",
                          !shouldTruncateOptions && "rounded-3xl",
                        )
                      }
                      value={option.value}
                      title={`${option.label}`}
                    >
                      {() => (
                        <>
                          {option.icon &&
                            React.createElement(option.icon, {
                              className: twMerge(
                                "w-4 h-4 flex-shrink-0 text-grey-dark",
                                ((isMultiple &&
                                  Array.isArray(value) &&
                                  value.includes(option.value)) ||
                                  option.value === value) &&
                                  "text-blue-dark",
                                option.disabled && "text-grey-hover",
                              ),
                            })}
                          <span
                            className={twMerge(
                              "font-normal",
                              ((isMultiple &&
                                Array.isArray(value) &&
                                value.includes(option.value)) ||
                                option.value === value) &&
                                "font-medium",
                              shouldTruncateOptions && "truncate",
                            )}
                          >
                            {option.label}
                          </span>
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                  {!options.length && (
                    <div className="flex h-10 items-center justify-center">
                      <h5>Rezultatų nėra.</h5>
                    </div>
                  )}
                </Listbox.Options>
              </FloatingPortal>
            </div>
          </div>
        )}
      </Listbox>
      {hint && !error && <span className="text-sm text-grey-dark">{hint}</span>}
      {error && (
        <span className="break-all text-sm text-red-dark">{error}</span>
      )}
    </div>
  );
};
